<template>
  <div class="meta-block">
    <h4 class="meta-subtitle">Points</h4>

    <ul v-if="rows.length" class="vf-points">
      <li
        v-for="row in rows"
        :key="row.id"
        class="vf-points__row"
        :class="{ 'is-active': row.isActive }"
        @click="$emit('select', row.id)"
      >
        <!-- Status glyph. Redundant with the chip text on purpose: status is
             carried by icon + text, never by colour alone. -->
        <span class="box" :class="'box--' + row.status.glyph" aria-hidden="true">
          <span v-if="row.status.glyph === 'pass'">✓</span>
          <span v-else-if="row.status.glyph === 'fail'">✕</span>
          <span v-else class="box-dot"></span>
        </span>
        <span class="pt-label-cell">
          <span
            class="pt-marker-swatch"
            :style="{ '--point-marker-color': row.markerColor }"
            aria-hidden="true"
          ></span>
          <input
            v-if="editingId === row.id"
            ref="renameInput"
            v-model="nameDraft"
            :data-point-id="row.id"
            class="pt-name-input"
            type="text"
            maxlength="48"
            :aria-label="`Name for ${row.label}`"
            @click.stop
            @keydown.enter.prevent="commitRename(row)"
            @keydown.esc.prevent="cancelRename"
            @blur="commitRename(row)"
          />
          <span v-else class="pt-label">{{ row.label }}</span>
        </span>
        <StatusChip
          class="pt-chip"
          :label="row.status.label"
          :variant="row.status.variant"
        />
        <button
          type="button"
          class="pt-eye"
          :class="{ 'is-hidden': row.markersHidden }"
          :title="`${row.markersHidden ? 'Show' : 'Hide'} markers for ${row.label}`"
          :aria-label="`${row.markersHidden ? 'Show' : 'Hide'} markers for ${row.label}`"
          :aria-pressed="row.markersHidden ? 'false' : 'true'"
          @click.stop="$emit('toggle-markers', row.id)"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path
              v-if="!row.markersHidden"
              d="M1.5 8s2.2-4 6.5-4 6.5 4 6.5 4-2.2 4-6.5 4S1.5 8 1.5 8Z M8 6.1A1.9 1.9 0 1 1 8 9.9 1.9 1.9 0 0 1 8 6.1Z"
            />
            <path
              v-else
              d="M2 2l12 12 M3.1 5.2C2 6.3 1.5 8 1.5 8s2.2 4 6.5 4c1.1 0 2.1-.3 2.9-.7 M6 4.3A7 7 0 0 1 8 4c4.3 0 6.5 4 6.5 4s-.6 1.1-1.7 2.1"
            />
          </svg>
        </button>
        <button
          type="button"
          class="pt-edit"
          :title="'Rename ' + row.label"
          :aria-label="'Rename ' + row.label"
          @click.stop="startRename(row)"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path
              d="M3 11.7 L2.5 14 L4.8 13.5 L12.7 5.6 L10.9 3.8 Z
                 M9.9 4.8 L11.7 6.6 M10.9 3.8 L12 2.7
                 A1.3 1.3 0 0 1 13.8 2.7 L13.8 2.7
                 A1.3 1.3 0 0 1 13.8 4.5 L12.7 5.6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="pt-del"
          :title="'Delete ' + row.label"
          :aria-label="'Delete ' + row.label"
          @click.stop="$emit('remove', row.id)"
        >
          <svg
            viewBox="0 0 16 16"
            width="14"
            height="14"
            aria-hidden="true"
          >
            <!-- Bin: lid, body, two content lines -->
            <path
              d="M2.6 4.2 L13.4 4.2 M6.2 4.2 L6.2 2.8 A0.6 0.6 0 0 1 6.8 2.2
                 L9.2 2.2 A0.6 0.6 0 0 1 9.8 2.8 L9.8 4.2
                 M3.9 4.2 L4.5 13.1 A0.8 0.8 0 0 0 5.3 13.8 L10.7 13.8
                 A0.8 0.8 0 0 0 11.5 13.1 L12.1 4.2"
              fill="none"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <line x1="6.7" y1="6.6" x2="6.9" y2="11.4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" />
            <line x1="9.3" y1="6.6" x2="9.1" y2="11.4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" />
          </svg>
        </button>
      </li>
    </ul>

    <p v-else class="vf-points__empty">No verification points yet.</p>

    <button type="button" class="vf-points__add" @click="$emit('add')">
      <span class="vf-points__plus" aria-hidden="true">+</span>
      Add point
    </button>
  </div>
</template>

<script>
/**
 * Left-panel list of verification points (AVA-475).
 *
 * Prop-driven leaf — points come from VERIFICATION/GET_POINTS_WITH_RESULTS at
 * the panel level, already decorated with their derived verdict. Nothing here
 * reads the store, and no result is recomputed locally.
 *
 * Accessibility: a point's status is carried by BOTH a glyph (✓ / ✕ / dot) and
 * the chip's text (PASS / FAIL / Pending) — never by colour alone.
 *
 * Verdict mapping is strictly three-way. `pass` is `null` for an incomplete
 * point, so a `pass ? PASS : FAIL` ternary would render a false FAIL — the one
 * thing the domain rules forbid. Branch on `=== true` / `=== false` instead.
 *
 * Emits `select(id)`, `add`, `remove(id)`, and `rename({ id, label })`; the
 * parent ContextPanel dispatches.
 */
import StatusChip from '@/features/Verification/components/shared/StatusChip.vue';
import { markerColorForPointId } from '@/features/Verification/utils/markerPalette';

// `variant` values must exist in StatusChip's validator
// (default | progress | pass | fail | primary) — there is no `pending` variant,
// so Pending maps to `progress`.
const STATUS_PASS = Object.freeze({ label: 'PASS', variant: 'pass', glyph: 'pass' });
const STATUS_FAIL = Object.freeze({ label: 'FAIL', variant: 'fail', glyph: 'fail' });
const STATUS_PENDING = Object.freeze({ label: 'Pending', variant: 'progress', glyph: 'pending' });

function statusFor(point) {
  if (point.pass === true) return STATUS_PASS;
  if (point.pass === false) return STATUS_FAIL;
  return STATUS_PENDING;
}

export default {
  name: 'PointList',
  components: { StatusChip },
  props: {
    points: {
      type: Array,
      default: () => [],
      // Expected item shape: { id, label, pass: true | false | null, … }
    },
    activeId: {
      type: String,
      default: null,
    },
    hiddenMarkerPointIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editingId: null,
      nameDraft: '',
    };
  },
  computed: {
    /** Presentation rows — derived, never mutating the incoming points. */
    rows() {
      return this.points.map((p) => ({
        id: p.id,
        label: p.label,
        isActive: p.id === this.activeId,
        status: statusFor(p),
        markerColor: markerColorForPointId(p.id),
        markersHidden: this.hiddenMarkerPointIds.includes(p.id),
      }));
    },
  },
  methods: {
    startRename(row) {
      this.editingId = row.id;
      this.nameDraft = row.label;
      this.$nextTick(() => {
        const refs = this.$refs.renameInput;
        const inputs = Array.isArray(refs) ? refs : [refs];
        const input = inputs.find((candidate) => candidate?.dataset?.pointId === row.id);
        input?.focus();
        input?.select();
      });
    },
    commitRename(row) {
      if (this.editingId !== row.id) return;
      const label = this.nameDraft.trim();
      this.editingId = null;
      this.nameDraft = '';
      this.$emit('rename', { id: row.id, label });
    },
    cancelRename() {
      this.editingId = null;
      this.nameDraft = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.meta-block {
  padding: 14px 0;
  border-bottom: 1px dashed $vf-line-soft;
}

.meta-subtitle {
  font-size: 11px;
  font-family: $vf-font-mono;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $vf-ink-3;
  font-weight: 500;
  margin: 0 0 10px 0;
}

// `vf-` prefix (as with `.vf-checklist`) keeps these clear of any global or
// Vuetify utility of the same name.
.vf-points {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__row {
    // glyph | label (flex) | chip | marker visibility | edit | delete
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto 26px 26px 26px;
    gap: 6px;
    align-items: center;
    padding: 6px 9px;
    border-radius: $vf-r-sm;
    cursor: pointer;
    // 1px transparent border so the active row's border doesn't shift layout.
    border: 1px solid transparent;
    transition: background 0.2s ease;

    &:hover {
      background: #f1f1f1;
    }

    &.is-active {
      background: rgba(3, 98, 76, 0.08);
      border-color: rgba(3, 98, 76, 0.25);
    }
  }

  &__empty {
    margin: 0;
    padding: 6px 9px;
    font-size: 11.5px;
    color: $vf-ink-3;
  }

  &__add {
    margin-top: 10px;
    width: 100%;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px dashed $vf-line-strong;
    border-radius: $vf-r-sm;
    background: transparent;
    color: $vf-ink-2;
    font-family: $vf-font-body;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $vf-primary;
      border-style: solid;
      color: $vf-primary;
      background: rgba(3, 98, 76, 0.04);
    }
  }

  &__plus {
    font-size: 15px;
    line-height: 1;
    font-weight: 600;
  }
}

// Status glyph box — mirrors the checklist's `.box` so both left-panel lists
// share one visual language.
.box {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid $vf-line-strong;
  display: grid;
  place-items: center;
  background: #fff;
  font-size: 11px;
  line-height: 1;
  color: #fff;

  &--pass {
    background: $vf-harvest;
    border-color: $vf-harvest;
  }

  &--fail {
    background: $vf-error;
    border-color: $vf-error;
  }

  &--pending {
    background: rgba(217, 119, 6, 0.12);
    border-color: rgba(217, 119, 6, 0.4);
    color: $vf-pending;
  }
}

// CSS-rendered dot — U+25CF carries baseline descent and sits ~1px low.
.box-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.pt-label-cell {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.pt-marker-swatch {
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border: 2px solid #fff;
  border-radius: 50%;
  background: var(--point-marker-color);
  box-shadow: 0 0 0 1px var(--point-marker-color);
}

.pt-label {
  font-family: $vf-font-mono;
  font-size: 12.5px;
  font-weight: 500;
  color: $vf-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pt-name-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 28px;
  padding: 0 7px;
  border: 1px solid $vf-primary;
  border-radius: $vf-r-sm;
  outline: none;
  background: #fff;
  color: $vf-ink;
  font-family: $vf-font-body;
  font-size: 12.5px;

  &:focus {
    box-shadow: 0 0 0 2px rgba(3, 98, 76, 0.15);
  }
}

.pt-chip {
  justify-self: end;
}

.pt-eye,
.pt-edit,
.pt-del {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: $vf-r-sm;
  background: transparent;
  color: $vf-ink-3;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(3, 98, 76, 0.08);
    color: $vf-primary;
  }
}

.pt-eye {
  svg {
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.15;
  }

  &.is-hidden {
    opacity: 0.55;
  }
}

.pt-del:hover {
  background: rgba(183, 28, 28, 0.08);
  color: $vf-error;
}
</style>
